import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
  fetchRequestData,
  handleSendOtp,
  handleVerifyOtpRequest,
} from "store/slices";

import usePermission from "hooks/usePermission";
import {
  getDataFromLocalStorage,
  getEventDate,
  getUserType,
  objectToFormData,
  paymentColor,
} from "utils/helpers";
import { icons, limit, tabs } from "utils/constants";

import Card from "components/Layout/Card";
import Table from "components/Layout/Table";
import Button from "components/form/Button";
import SeachInput from "components/form/SeachInput";
import ExportButton from "components/Layout/ExportButton";
import FilterDropdown from "components/Layout/FilterDropdown";

import "./EventManagement.scss";
import TabLayout from "components/Layout/TabLayout";
import BulkAction from "../Common/BulkAction";

const EventManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { type } = params;
  const memberType = getUserType();
  const userType = getDataFromLocalStorage('user_type');
  const { viewAccess, createAccess, updateAccess } = usePermission("Requests");

  const { eventTypeList } = useSelector((state) => ({
    eventTypeList: state.global.eventTypeList,
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [tableList, setTableList] = useState([]);
  const [childTabType, setChildTabType] = useState('0');
  const [searchText, setSearchText] = useState('');
  const [timer, setTimer] = useState('');
  const [searchPayload, setSearchPayload] = useState({
    event_name: '',
    event_type: '',
  });
  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit: limit,
    event_by_filter: '',
  });


  const getRequestsData = async (obj) => {
    setSelectedIds([])
    setTableList([])
    setFilterData((prev) => ({
      ...prev,
      total: 0,
    }));

    let forData = objectToFormData(obj);
    const response = await dispatch(fetchRequestData(forData));
    setTableList(response?.data?.requests || []);
    setFilterData({
      ...obj,
      total: response?.data?.result_count || 0,
    });
    setIsLoading(false);
  };

  const handelChangeSearch = (searchData) => {
    setIsLoading(true);
    let newData = filterData;
    setSearchPayload(searchData);
    newData = { ...newData, ...searchData, offset: 0 };
    setFilterData(newData);
    getRequestsData(newData);
  };

  const handelChangePagination = (offset, newLimit) => {
    setIsLoading(true);
    const updatedData = {
      ...filterData,
      ...searchPayload,
      offset,
      limit: newLimit ?? filterData.limit,
    };
    setFilterData(updatedData);
    getRequestsData(updatedData);
  };

  const handelChangeFilter = (val) => {
    setIsLoading(true);
    let newData = { ...filterData, ...searchPayload };
    newData = { ...newData, event_by_filter: val, offset: 0 };
    setFilterData(newData);
    getRequestsData(newData);
  };

  useEffect(() => {
    const payload = {
      ...filterData,
      ...searchPayload,
    };

    if (['0', '1'].includes(userType)) {
      payload.status = childTabType
    }
    setIsLoading(true);
    getRequestsData(payload);
  }, [childTabType]);

  const handleSearch = (e) => {
    let value = e?.target?.value;
    setSearchText(value);
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      const updatedSearchPayload = { ...searchPayload, event_name: value };
      setSearchPayload(updatedSearchPayload);
      const updatedFilterData = {
        ...filterData,
        offset: 0,
        ...updatedSearchPayload,
      };
      setIsLoading(true);
      setFilterData(updatedFilterData);
      getRequestsData(updatedFilterData);
    }, 800);
    setTimer(time);
  };

  const urlType = ['0', '1'].includes(userType)
    ? 'request-received'
    : 'raised-request';


  //BulkApproved
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);

  const handleSelectAllChange = () => {
    const newChecked = !isCheckAll;
    setIsCheckAll(newChecked);
    setSelectedIds(newChecked ? tableList.map(({ request_id }) => request_id) : []);
  };

  const handleCheckboxChange = (id, checked) => {
    setSelectedIds((prev) => {
      const updated = checked ? [...prev, id] : prev.filter((x) => x !== id);
      setIsCheckAll(updated.length === tableList.length);
      return updated;
    });
  };

  const header = [
    ...(childTabType === '0' && ['1'].includes(userType) ? [{ isSearch: false }] : []),
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: { options: eventTypeList, key: 'id', value: 'name' },
      searchInputName: 'event_type',
      title: 'Event Type',
    },
    {
      isSearch: true,
      searchInputName: 'event_name',
      title: 'Event Name',
    },
    {
      isSearch: true,
      isDatePicker: true,
      searchInputName: 'date',
      title: 'Date',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [
          { name: 'Pending' },
          { name: 'In-Progress' },
          { name: 'Approved' },
          { name: 'Rejected' },
        ],
        key: 'name',
        value: 'name',
      },
      searchInputName: 'request_status',
      title: 'Request Status',
    },
    {
      isSearch: false,
      searchLable: 'Edit/Delete',
      title: 'Action',
    },
  ];



  const rowData = [];
  tableList?.forEach((elem) => {
    let obj = [
      ...(childTabType === '0' && ['1'].includes(userType) //superAdmin
        ? [
          {
            value: (
              <div className="d-flex justify-content-center align-items-center">
                <input
                  type="checkbox"
                  className={`custom-checkbox ${selectedIds.includes(elem?.request_id)
                    ? 'activeChecked'
                    : 'activeUnchecked'
                    }`}
                  onChange={(e) =>
                    handleCheckboxChange(elem?.request_id, e.target.checked)
                  }
                  checked={selectedIds.includes(elem?.request_id)}
                />
              </div>
            ),
          },
        ]
        : []),
      {
        value: elem.event_type,
      },
      {
        value: <div className="pointer color-new-car">{elem.event_name}</div>,
      },
      {
        value: (
          <span>
            <div>{getEventDate(elem.start_date, elem.end_date)}</div>
            {elem?.country_name && (
              <div>{`${elem?.city_name ? `${elem?.city_name},` : ''} ${elem?.country_name
                }`}</div>
            )}
          </span>
        ),
      },
      {
        value: <div className={paymentColor(elem?.request_status)}>{elem?.request_status}</div>,
      },
      {
        value: (
          <span className="action-icon-buttons">
            {viewAccess === '1' && (
              <Button
                btnStyle="light-outline"
                icon={<i className="bi bi-eye" />}
                className="me-2"
                onClick={() => {
                  navigate(
                    `/${memberType}/${urlType}/${type}/events/view-event/${elem?.request_id}`
                  );
                }}
                isSquare
              />
            )}
            {updateAccess === '1' && elem?.status === 'Pending' && (
              <Button
                btnStyle="light-outline"
                icon={<img src={icons.edit} alt="edit" />}
                className="me-2"
                onClick={() => {
                  navigate(
                    `/${memberType}/${urlType}/${type}/events/edit-event/${elem.request_id}`
                  );
                }}
                isSquare
              />
            )}
          </span>
        ),
      },
    ];

    rowData.push({ data: obj });
  });


  return (
    <div id="event-management-container">
      <Card className="cps-20 cpe-20 cpb-20">
        {["0", "1"].includes(userType) && (
          <div className="cpt-14">
            <TabLayout
              tabs={tabs}
              activeKey={childTabType}
              onChange={setChildTabType}
            />
          </div>
        )}

        {selectedIds.length === 0 ? (
          <div className="d-flex justify-content-between align-items-center cpt-20 cpb-20">
            <div className="table-title">All Events ({filterData?.total})</div>
            <div className="d-flex">
              <div style={{ width: '150px' }} className="me-3">
                <SeachInput
                  placeholder="Search"
                  value={searchText}
                  onChange={handleSearch}
                  className="small-text-input"
                  iconClassName="small-search-icon"
                />
              </div>

              {['6'].includes(userType) && (
                <>
                  <div className="d-flex me-3">
                    <FilterDropdown
                      isHideAll
                      list={[
                        { id: '1', name: 'IFERP Events' },
                        { id: '2', name: 'Self Driven Events' },
                      ]}
                      handelChangeFilter={handelChangeFilter}
                    />
                  </div>

                  <div className="d-flex me-3">
                    <ExportButton
                      exportAPI={fetchRequestData}
                      payload={objectToFormData({
                        ...filterData,
                        ...searchPayload,
                        export_status: 1,
                      })}
                    />
                  </div>

                  {createAccess === '1' && (
                    <Button
                      onClick={() => {
                        navigate(`/${memberType}/${type}/events/add-event`);
                      }}
                      text="+ Add Event"
                      btnStyle="primary-outline"
                      className="h-35 text-14-500 cps-10 cpe-10"
                      isSquare
                    />
                  )}
                </>
              )}
            </div>
          </div>) :
          childTabType === "0" && (
            <BulkAction
              isCheckAll={isCheckAll}
              handleSelectAllChange={handleSelectAllChange}
              isLoading={isLoading}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              handleSendOtp={handleSendOtp}
              handleVerifyOtpRequest={handleVerifyOtpRequest}
              typeTitle="Event"
              onSuccess={() => getRequestsData({ ...filterData, ...searchPayload })}
            />
          )}

        <Table
          isLoading={isLoading}
          header={header}
          rowData={rowData}
          filterData={filterData}
          searchPayload={searchPayload}
          searchInputChange={handelChangeSearch}
          changeOffset={handelChangePagination}
          maxHeight="60vh"
          showRowsLimit={['0', '1'].includes(userType)}
        />
      </Card>
    </div>
  );
};

export default EventManagement;




// import React, { useEffect, useState, useCallback } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate, useParams } from "react-router-dom";

// import {
//   fetchRequestData,
//   handleSendOtp,
//   handleVerifyOtpRequest,
//   showSuccess,
//   throwError,
// } from "store/slices";

// import usePermission from "hooks/usePermission";
// import {
//   getDataFromLocalStorage,
//   getEventDate,
//   getUserType,
//   objectToFormData,
//   paymentColor,
// } from "utils/helpers";
// import { icons, limit } from "utils/constants";

// import Card from "components/Layout/Card";
// import Table from "components/Layout/Table";
// import Button from "components/form/Button";
// import CheckBox from "components/form/CheckBox";
// import SearchInput from "components/form/SeachInput";
// import ExportButton from "components/Layout/ExportButton";
// import FilterDropdown from "components/Layout/FilterDropdown";
// import ActionFormPopup from "../CertificateManagement/ParticipantsRequest/ActionFormPopup";
// import RequestVerificationModal from "../Common/RequestVerificationModal";
// import VerifyOtpModel from "../Common/VerifyOtpModel";

// import "./EventManagement.scss";

// const EventManagement = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const { type } = useParams();
//   const { eventTypeList } = useSelector((state) => state.global);

//   const { viewAccess, createAccess, updateAccess } = usePermission("Requests");
//   const memberType = getUserType();
//   const userType = getDataFromLocalStorage("user_type");


//   const [isLoading, setIsLoading] = useState(false);
//   const [tableList, setTableList] = useState([]);
//   const [childTabType, setChildTabType] = useState("1"); // 1 = Pending, 2 = Completed
//   const [searchText, setSearchText] = useState("");
//   const [selectedIds, setSelectedIds] = useState([]);
//   const [isCheckAll, setIsCheckAll] = useState(false);
//   const [timer, setTimer] = useState(null);

//   const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] = useState(false);
//   const [showOtpDialog, setShowOtpDialog] = useState(false);
//   const [openActionForm, setOpenActionForm] = useState(false);

//   const [otpData, setOtpData] = useState({});
//   const [btnLoading, setBtnLoading] = useState(false);
//   const [otpVerification, setOtpVerification] = useState(false);
//   const [isRejecting, setIsRejecting] = useState(false);

//   const [searchPayload, setSearchPayload] = useState({ event_name: "", event_type: "" });
//   const [filterData, setFilterData] = useState({
//     total: 0,
//     offset: 0,
//     limit,
//     event_by_filter: "",
//   });


//   const fetchData = useCallback(
//     async (payload) => {
//       setIsLoading(true);
//       const formData = objectToFormData(payload);
//       const response = await dispatch(fetchRequestData(formData));
//       if (response?.status === 200) {
//         setTableList(response?.data?.requests || []);
//         setFilterData((prev) => ({
//           ...prev,
//           total: response?.data?.result_count || 0,
//         }));
//       } else {
//         dispatch(throwError(response));
//       }
//       setIsLoading(false);
//     },
//     [dispatch]
//   );

//   const handleSearch = (e) => {
//     const { value } = e.target;
//     setSearchText(value);
//     clearTimeout(timer);

//     const newTimer = setTimeout(() => {
//       const updatedPayload = { ...searchPayload, event_name: value };
//       setSearchPayload(updatedPayload);
//       fetchData({ ...filterData, ...updatedPayload, offset: 0 });
//     }, 600);

//     setTimer(newTimer);
//   };

//   useEffect(() => () => clearTimeout(timer), [timer]);


//   const handleChangeSearch = (data) => {
//     const updated = { ...filterData, ...data, offset: 0 };
//     setSearchPayload(data);
//     fetchData(updated);
//   };

//   const handleChangePagination = (offset, newLimit) => {
//     fetchData({
//       ...filterData,
//       ...searchPayload,
//       offset,
//       limit: newLimit ?? filterData.limit,
//     });
//   };

//   const handleChangeFilter = (val) =>
//     fetchData({ ...filterData, ...searchPayload, event_by_filter: val, offset: 0 });

//   useEffect(() => {
//     const payload = {
//       ...filterData,
//       ...searchPayload,
//     };

//     if (['0', '1'].includes(userType)) {
//       payload.status = childTabType === '1' ? '0' : '1';
//     }
//     setIsLoading(true);
//     fetchData(payload);
//   }, [childTabType]);


//   const handleSelectAllChange = () => {
//     const newChecked = !isCheckAll;
//     setIsCheckAll(newChecked);
//     setSelectedIds(newChecked ? tableList.map(({ request_id }) => request_id) : []);
//   };

//   const handleCheckboxChange = (id, checked) => {
//     setSelectedIds((prev) => {
//       const updated = checked ? [...prev, id] : prev.filter((x) => x !== id);
//       setIsCheckAll(updated.length === tableList.length);
//       return updated;
//     });
//   };

//   /* ------------------------------------------
//    * Approve / Reject
//    * ------------------------------------------ */
//   const initialValues = { comment: "", document: "" };

//   const sendOtpRequest = async () => {
//     setBtnLoading(true);
//     const payload = objectToFormData({ request_ids: selectedIds });
//     const response = await dispatch(handleSendOtp(payload));
//     setBtnLoading(false);

//     if (response.status === 200) {
//       setShowOtpDialog(true);
//       setOpenActionForm(false);
//       dispatch(showSuccess(response?.message));
//     } else dispatch(throwError(response));
//   };

//   const handleReject = async (values, setErrors) => {
//     setIsRejecting(true);
//     const { comment, document } = values;

//     if (!comment.trim() && !document) {
//       const errMsg = "Either a comment or a document is required";
//       setErrors({ comment: errMsg, document: errMsg });
//       return;
//     }

//     setOtpData({ comment, document, action: "2" });
//     await sendOtpRequest();
//   };

//   const handleApproveRequest = async (values) => {
//     const { comment, document } = values;
//     setOtpData({ comment, document, action: "1" });

//     if (userType === "1") await sendOtpRequest();
//     else {
//       setIsOpenConfirmationDialog(true);
//       setOpenActionForm(false);
//     }
//   };

//   const handleVerifyOtp = async (otp) => {
//     setOtpVerification(true);

//     const payload = objectToFormData({
//       request_ids: selectedIds,
//       ...(otp && { otp }),
//       document: otpData?.document,
//       comment: otpData?.comment,
//       request_status: otpData?.action,
//       request_data: 1,
//     });

//     const response = await dispatch(handleVerifyOtpRequest(payload));
//     setOtpVerification(false);

//     if (response.status === 200) {
//       setShowOtpDialog(false);
//       setIsOpenConfirmationDialog(false);
//       dispatch(showSuccess(response.message));
//       setSelectedIds([]);
//     } else dispatch(throwError(response));
//   };

//   /* ------------------------------------------
//    * Table Config
//    * ------------------------------------------ */
//   const header = [
//     ...(childTabType === "1" ? [{ isSearch: false }] : []),
//     {
//       isSearch: true,
//       isSearchDropdown: true,
//       dropdownOptions: { options: eventTypeList, key: "id", value: "name" },
//       searchInputName: "event_type",
//       title: "Event Type",
//     },
//     { isSearch: true, searchInputName: "event_name", title: "Event Name" },
//     { isSearch: true, isDatePicker: true, searchInputName: "date", title: "Date" },
//     {
//       isSearch: true,
//       isSearchDropdown: true,
//       dropdownOptions: {
//         options: ["Pending", "In-Progress", "Approved", "Rejected"].map((name) => ({ name })),
//         key: "name",
//         value: "name",
//       },
//       searchInputName: "request_status",
//       title: "Request Status",
//     },
//     { isSearch: false, searchLabel: "Edit/Delete", title: "Action" },
//   ];

//   const rowData = tableList.map((elem) => {
//     const basePath = `/${memberType}/${["0", "1"].includes(userType) ? "request-received" : "raised-request"}/${type}/events`;

//     const isChecked = selectedIds.includes(elem?.request_id);

//     return {
//       data: [
//         ...(childTabType === "1" && userType !== "6"
//           ? [
//             {
//               value: (
//                 <div className="d-flex justify-content-center align-items-center">
//                   <input
//                     type="checkbox"
//                     className={`custom-checkbox ${isChecked ? "activeChecked" : "activeUnchecked"}`}
//                     checked={isChecked}
//                     onChange={(e) => handleCheckboxChange(elem?.request_id, e.target.checked)}
//                   />
//                 </div>
//               ),
//             },
//           ]
//           : []),
//         { value: elem.event_type },
//         { value: <div className="pointer color-new-car">{elem.event_name}</div> },
//         {
//           value: (
//             <>
//               <div>{getEventDate(elem.start_date, elem.end_date)}</div>
//               {elem?.country_name && (
//                 <div>{`${elem?.city_name ? `${elem.city_name}, ` : ""}${elem.country_name}`}</div>
//               )}
//             </>
//           ),
//         },
//         { value: <div className={paymentColor(elem.request_status)}>{elem.request_status}</div> },
//         {
//           value: (
//             <div className="action-icon-buttons">
//               {viewAccess === "1" && (
//                 <Button
//                   btnStyle="light-outline"
//                   icon={<i className="bi bi-eye" />}
//                   className="me-2"
//                   onClick={() => navigate(`${basePath}/view-event/${elem.request_id}`)}
//                   isSquare
//                 />
//               )}
//               {updateAccess === "1" && elem.status === "Pending" && (
//                 <Button
//                   btnStyle="light-outline"
//                   icon={<img src={icons.edit} alt="edit" />}
//                   className="me-2"
//                   onClick={() => navigate(`${basePath}/edit-event/${elem.request_id}`)}
//                   isSquare
//                 />
//               )}
//             </div>
//           ),
//         },
//       ],
//     };
//   });


//   return (
//     <div id="event-management-container">
//       {/* Action Form Popup */}
//       {openActionForm && (
//         <ActionFormPopup
//           showApproveDialog={openActionForm}
//           onHide={() => setOpenActionForm(false)}
//           setIsRejecting={setIsRejecting}
//           isRejecting={isRejecting}
//           handleApproveRequest={handleApproveRequest}
//           handleReject={handleReject}
//           initialValues={initialValues}
//           btnLoading={btnLoading}
//         />
//       )}

//       {/* Confirmation Modal */}
//       {isOpenConfirmationDialog && (
//         <RequestVerificationModal
//           handleConfirmation={() => handleVerifyOtp()}
//           onHide={() => setIsOpenConfirmationDialog(false)}
//           title="You want to share the approve request to super admin"
//           isLoading={otpVerification}
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpDialog && (
//         <VerifyOtpModel
//           title="Participants Request"
//           showApproveDialog={showOtpDialog}
//           onHide={() => setShowOtpDialog(false)}
//           onVerify={handleVerifyOtp}
//           sendOtpRequest={sendOtpRequest}
//           otpVerification={otpVerification}
//         />
//       )}

//       <Card className="cps-20 cpe-20 cpb-20">
//         {/* Tabs */}
//         {["0", "1"].includes(userType) && (
//           <div className="event-management-tab cpt-14">
//             {["1", "2"].map((tab) => (
//               <div
//                 key={tab}
//                 className={`tab ${childTabType === tab ? "active" : ""}`}
//                 onClick={() => {
//                   setChildTabType(tab)
//                   setSelectedIds([])
//                   setTableList([])
//                   setFilterData((prev) => ({
//                     ...prev,
//                     total: 0,
//                   }));
//                 }}
//               >
//                 {tab === "1" ? "Pending" : "Completed"}
//               </div>
//             ))}
//           </div>
//         )}

//         {/* Header or Bulk Actions */}
//         {selectedIds.length === 0 ? (
//           <div className="d-flex justify-content-between align-items-center cpt-28 cpb-28">
//             <div className="table-title">All Events ({filterData.total})</div>
//             <div className="d-flex align-items-center gap-3">
//               <div style={{ width: "150px" }}>
//                 <SearchInput
//                   placeholder="Search"
//                   value={searchText}
//                   onChange={handleSearch}
//                   className="small-text-input"
//                   iconClassName="small-search-icon"
//                 />
//               </div>

//               {userType === "6" && (
//                 <>
//                   <FilterDropdown
//                     isHideAll
//                     list={[
//                       { id: "1", name: "IFERP Events" },
//                       { id: "2", name: "Self Driven Events" },
//                     ]}
//                     handelChangeFilter={handleChangeFilter}
//                   />
//                   <ExportButton
//                     exportAPI={fetchRequestData}
//                     payload={objectToFormData({
//                       ...filterData,
//                       ...searchPayload,
//                       export_status: 1,
//                     })}
//                   />
//                   {createAccess === "1" && (
//                     <Button
//                       text="+ Add Event"
//                       btnStyle="primary-outline"
//                       className="h-35 text-14-500 cps-10 cpe-10"
//                       onClick={() => navigate(`/${memberType}/${type}/events/add-event`)}
//                       isSquare
//                     />
//                   )}
//                 </>
//               )}
//             </div>
//           </div>
//         ) : (
//           childTabType === "1" && (
//             <div className="d-flex gap-2 h-35 cmt-10 cmb-10">
//               <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
//                 <CheckBox type="ACTIVE" isChecked={isCheckAll} onClick={handleSelectAllChange} />
//                 <span>Select All Event</span>
//               </div>

//               {createAccess === "1" && (
//                 <>
//                   <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
//                     <Button
//                       text={["0", "1"].includes(userType) ? "Approve" : "Send Request"}
//                       className="h-35 border-0"
//                       disabled={isLoading}
//                       onClick={() => setOpenActionForm(true)}
//                     />
//                   </div>
//                   {["0", "1"].includes(userType) && (
//                     <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
//                       <Button
//                         text="Reject"
//                         className="h-35 border-0"
//                         onClick={() => setOpenActionForm(true)}
//                       />
//                     </div>
//                   )}
//                 </>
//               )}
//             </div>
//           )
//         )}

//         {/* Table */}
//         <Table
//           isLoading={isLoading}
//           header={header}
//           rowData={rowData}
//           filterData={filterData}
//           searchPayload={searchPayload}
//           searchInputChange={handleChangeSearch}
//           changeOffset={handleChangePagination}
//           maxHeight="60vh"
//           showRowsLimit={["0", "1"].includes(userType)}
//         />
//       </Card>
//     </div>
//   );
// };

// export default EventManagement;